import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Platform, View } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { useLocalSearchParams, useRouter } from 'expo-router'
import Head from 'expo-router/head'
import { useContentfulLocale } from '@/src/hooks/useContentfulLocale'
import {
  ContentSession,
  sessionQueryOptions,
} from '@/src/queries/library-queries'
import { getCurrentYearWeek } from '@/src/utils/year-week'
import { useSuspenseQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import type { DashboardExerciseType } from '../../../types/dashboard'
import { ScreenLoadingFallback } from '../../../components/ui/ScreenLoadingFallback'
import {
  buildSessionLifecycleProperties,
  buildStepLifecycleProperties,
  MixpanelEvents,
  trackEvent,
} from '../../../services/analytics'
import { addBreadcrumb, captureException } from '../../../utils/sentry'
import { SessionScreenContent } from '../components/SessionScreenContent'
import { SessionScreenHeader } from '../components/SessionScreenHeader'
import { SessionScreenMessageState } from '../components/SessionScreenState'
import { StepSelector } from '../components/StepSelector'
import { useSessionCompletionMutation } from '../hooks/useSessionCompletionMutation'
import { normalizeMediaUrl } from '../utils/media'
import {
  isSessionPrefetchComplete,
  prefetchSessionMedia,
} from '../utils/prefetch-session-media'
import { markSessionFinishEligible } from '../utils/session-finish-guard'
import {
  buildSessionStepKeys,
  normalizeSessionParam,
  resolveInitialVisualAspectRatio,
  resolveSessionSource,
} from '../utils/session-screen-helpers'

const DEFAULT_EXERCISE_TIME_SPENT = 15
const SESSION_CLOSE_FALLBACK_ROUTE = '/(protected)/(account)/(tabs)' as const
const SESSION_CLOSE_LIBRARY_ROUTE =
  '/(protected)/(account)/(tabs)/library' as const
const SESSION_FINISH_ROUTE = '/session/finish' as const

type SessionData = ContentSession

function useSessionMediaPrefetchStatus(
  session: SessionData | undefined,
  sessionId: string,
) {
  const [completedPrefetchSessionId, setCompletedPrefetchSessionId] = useState<
    string | null
  >(() => (isSessionPrefetchComplete(sessionId) ? sessionId : null))

  useEffect(() => {
    let isMounted = true

    if (session == null || isSessionPrefetchComplete(sessionId)) {
      return () => {
        isMounted = false
      }
    }

    prefetchSessionMedia(session)
      .catch((e) => {
        addBreadcrumb(
          'session',
          'media_prefetch_failed',
          {
            sessionId,
          },
          'warning',
        )
        captureException(e, {
          feature: 'session',
          tags: { flow: 'media_prefetch', sessionId },
        })
      })
      .finally(() => {
        if (isMounted) {
          setCompletedPrefetchSessionId(sessionId)
        }
      })

    return () => {
      isMounted = false
    }
  }, [session, sessionId])

  return (
    completedPrefetchSessionId === sessionId ||
    isSessionPrefetchComplete(sessionId)
  )
}

function useSessionStepTracking({
  currentStepIndex,
  exerciseType,
  programId,
  session,
  subjectId,
  weekId,
}: {
  currentStepIndex: number
  exerciseType: string | undefined
  programId: string | undefined
  session: SessionData | undefined
  subjectId: string | undefined
  weekId: string | undefined
}) {
  const trackedSessionIdRef = useRef<string | null>(null)
  const trackedInitialStepStartedRef = useRef<string | null>(null)

  useEffect(() => {
    if (session == null || trackedSessionIdRef.current === session.id) {
      return
    }

    trackedSessionIdRef.current = session.id
    trackEvent(
      exerciseType === 'daily'
        ? MixpanelEvents.DailyRoutineStarted
        : MixpanelEvents.CourseStarted,
      buildSessionLifecycleProperties({
        sessionId: session.id,
        sessionName: session.title ?? '',
        weekId,
        subjectId,
        programId,
        exerciseType: exerciseType as DashboardExerciseType | undefined,
      }),
    )
  }, [exerciseType, programId, session, subjectId, weekId])

  const trackStepFinished = useCallback(
    (stepIndex: number) => {
      if (session == null) return

      const step = session.steps[stepIndex]
      if (step == null) return

      trackEvent(
        MixpanelEvents.CourseStepFinished,
        buildStepLifecycleProperties({
          sessionId: session.id,
          stepId: step.id,
          sessionName: session.title ?? '',
          stepName: step.title ?? '',
        }),
      )
    },
    [session],
  )

  const trackStepStarted = useCallback(
    (stepIndex: number) => {
      if (session == null) return

      const step = session.steps[stepIndex]
      if (step == null) return

      trackEvent(
        MixpanelEvents.CourseStepStarted,
        buildStepLifecycleProperties({
          sessionId: session.id,
          stepId: step.id,
          sessionName: session.title ?? '',
          stepName: step.title ?? '',
        }),
      )
    },
    [session],
  )

  useEffect(() => {
    const currentStep = session?.steps[currentStepIndex]
    if (session == null || currentStep == null || !session.steps.length) {
      return
    }

    if (trackedInitialStepStartedRef.current === session.id) {
      return
    }

    trackedInitialStepStartedRef.current = session.id
    trackStepStarted(currentStepIndex)
  }, [currentStepIndex, session, trackStepStarted])

  return { trackStepFinished, trackStepStarted }
}

export default function SessionScreen() {
  const {
    id: idParam,
    source: sourceParam,
    weekId: weekIdParam,
    subjectId: subjectIdParam,
    programId: programIdParam,
    exerciseType: exerciseTypeParam,
  } = useLocalSearchParams<{
    id?: string | string[]
    source?: string | string[]
    weekId?: string | string[]
    subjectId?: string | string[]
    programId?: string | string[]
    exerciseType?: string | string[]
  }>()
  const { back, canGoBack, replace } = useRouter()
  const insets = useSafeAreaInsets()
  const sessionId = normalizeSessionParam(idParam)
  if (sessionId == null)
    throw new Error('Session ID is required: ?id=<session_id>')
  const source = resolveSessionSource(sourceParam)
  const weekId = normalizeSessionParam(weekIdParam)
  const subjectId = normalizeSessionParam(subjectIdParam)
  const programId = normalizeSessionParam(programIdParam)
  const exerciseType = normalizeSessionParam(exerciseTypeParam)
  const yearWeek = getCurrentYearWeek()
  const sessionCompletionMutation = useSessionCompletionMutation(yearWeek)

  const { t } = useTranslation(['session', 'common'])
  const language = useContentfulLocale()

  const {
    data: session,
    isLoading,
    error,
  } = useSuspenseQuery(sessionQueryOptions(sessionId, language))
  const [currentStepIndex, setCurrentStepIndex] = useState(0)
  const [isStepSelectorVisible, setIsStepSelectorVisible] = useState(false)
  const [isAudioLoading, setIsAudioLoading] = useState(false)
  const isMediaPrefetchComplete = useSessionMediaPrefetchStatus(
    session,
    sessionId,
  )

  const currentStep = session?.steps[currentStepIndex]

  const mediaItems = useMemo(() => {
    return currentStep?.media ?? []
  }, [currentStep?.media])

  const visualMedia = mediaItems.find((m) => {
    const ct = m.contentType.toLowerCase()
    if (ct == null) return false
    return ct.startsWith('video/') || ct.startsWith('image/')
  })

  const audioMedia = mediaItems.find((m) => {
    const ct = m.contentType.toLowerCase()
    if (ct == null) return false
    return ct.startsWith('audio/')
  })

  // Visual Media Setup
  const rawVisualUrl = visualMedia?.url ?? undefined
  const visualUrl = normalizeMediaUrl(rawVisualUrl)
  const visualContentType = visualMedia?.contentType ?? ''
  const visualFileName = visualMedia?.fileName
  const isVideo = visualContentType.startsWith('video/')

  // Audio Media Setup
  const rawAudioUrl = audioMedia?.url ?? undefined
  const audioUrl = normalizeMediaUrl(rawAudioUrl)

  const isLastStep =
    !!session?.steps.length && currentStepIndex === session.steps.length - 1
  const footerBottomPadding = Math.max(insets.bottom + 16, 32)
  const scrollContentBottomPadding = 180 + insets.bottom
  const {
    fallbackAspectRatio: visualFallbackAspectRatio,
    initialAspectRatio: initialVisualAspectRatio,
  } = useMemo(
    () =>
      resolveInitialVisualAspectRatio({
        isVideo,
        isWeb: Platform.OS === 'web',
        visualMedia,
      }),
    [isVideo, visualMedia],
  )
  const sessionStepKeys = useMemo(
    () => buildSessionStepKeys(session?.steps),
    [session?.steps],
  )
  const { trackStepFinished, trackStepStarted } = useSessionStepTracking({
    currentStepIndex,
    exerciseType,
    programId,
    session,
    subjectId,
    weekId,
  })

  const handleClose = () => {
    if (source === 'library') {
      replace(SESSION_CLOSE_LIBRARY_ROUTE)
      return
    }

    if (canGoBack()) {
      back()
      return
    }

    replace(SESSION_CLOSE_FALLBACK_ROUTE)
  }

  const handleNext = async () => {
    if (!session?.steps.length) return

    const totalSteps = session.steps.length

    if (currentStepIndex < totalSteps - 1) {
      const nextIndex = currentStepIndex + 1
      trackStepFinished(currentStepIndex)
      trackStepStarted(nextIndex)
      setCurrentStepIndex(nextIndex)
      return
    }

    if (sessionCompletionMutation.isPending) {
      return
    }

    trackStepFinished(currentStepIndex)
    const isFinishEligible = await markSessionFinishEligible({
      sessionId,
    }).catch((err) => {
      console.error('Failed to prepare session finish guard:', err)
      captureException(err, {
        feature: 'session',
        tags: { flow: 'save_progress', sessionId },
      })
      return false
    })

    if (!isFinishEligible) {
      const finishGuardError = new Error(
        'Failed to prepare session finish guard',
      )
      console.error(finishGuardError.message)
      captureException(finishGuardError, {
        feature: 'session',
        tags: { flow: 'save_progress', sessionId },
      })
      return
    }

    await sessionCompletionMutation.mutateAsync({
      sessionId,
      exerciseType,
      timeSpent: session.lengthMinutes ?? DEFAULT_EXERCISE_TIME_SPENT,
      context: {
        contentfulProgramId: programId ?? null,
        contentfulSubjectId: subjectId ?? null,
        contentfulWeekId: weekId ?? null,
      },
    })

    replace({
      pathname: SESSION_FINISH_ROUTE,
      params: {
        id: sessionId,
        source,
        title: session.title ?? '',
        weekId,
        subjectId,
        programId,
        exerciseType,
      },
    })
  }

  const handleSelectStep = async (stepIndex: number) => {
    if (session == null || stepIndex === currentStepIndex) return

    trackStepStarted(stepIndex)
    setCurrentStepIndex(stepIndex)
  }

  if (isLoading || (session != null && !isMediaPrefetchComplete)) {
    return <ScreenLoadingFallback />
  }

  if (error != null || session == null) {
    return (
      <SessionScreenMessageState
        message={t('session:load_error')}
        actionLabel={t('common:back')}
        onPress={handleClose}
      />
    )
  }

  if (currentStep == null) {
    return (
      <SessionScreenMessageState
        message={t('session:step_not_found')}
        actionLabel={t('session:exit')}
        onPress={handleClose}
      />
    )
  }

  return (
    <View className="flex-1 bg-background-primary">
      <Head>
        <title>{t('common:page_title_session')}</title>
      </Head>
      <SessionScreenHeader
        onClose={handleClose}
        onOpenStepSelector={() => setIsStepSelectorVisible(true)}
        title={session.title ?? ''}
      />

      <StepSelector
        isVisible={isStepSelectorVisible}
        onClose={() => setIsStepSelectorVisible(false)}
        steps={session.steps}
        activeStepIndex={currentStepIndex}
        onSelectStep={handleSelectStep}
      />

      <SessionScreenContent
        audioUrl={audioUrl}
        currentStepIndex={currentStepIndex}
        description={currentStep.description ?? ''}
        footerBottomPadding={footerBottomPadding}
        initialVisualAspectRatio={initialVisualAspectRatio}
        onAudioLoadingChange={setIsAudioLoading}
        onNextPress={handleNext}
        primaryLabel={isLastStep ? t('session:finish') : t('session:next')}
        scrollContentBottomPadding={scrollContentBottomPadding}
        status={{
          isAudioLoading,
          isLastStep,
          isNextDisabled: sessionCompletionMutation.isPending,
          isSubmitting: sessionCompletionMutation.isPending,
        }}
        stepKeys={sessionStepKeys}
        stepTitle={currentStep.title ?? ''}
        visualContentType={visualContentType}
        visualDiagnostics={{
          assetFileName: visualFileName ?? null,
          contentType: visualContentType,
          sessionId,
          stepIndex: currentStepIndex,
          stepTitle: currentStep.title,
        }}
        visualFallbackAspectRatio={visualFallbackAspectRatio}
        visualUrl={visualUrl}
      />
    </View>
  )
}
